import {
  LitElement,
  html,
  css,
  utils,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    closeAutofocus: {
      type: Boolean,
      attribute: 'close-autofocus',
    },
  }

  constructor() {
    super()
    this.id = null
    this.open = false
    this.closeAutofocus = true
  }

  updated(changedProperties) {
    if (
      changedProperties.has('open') &&
      this.open === true &&
      this.closeAutofocus
    ) {
      this.shadowRoot.querySelector('.button')?.focus()
    }
  }

  requestUpdate(name, oldValue) {
    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    const docIsReady = setInterval(() => {
      if (document.readyState !== 'complete') {
        return
      } else {
        clearInterval(docIsReady)
        this.classList.remove('hidden')
      }
    }, 100)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()
  }

  handleEscKey(e) {
    if (e.key === 'Escape' && this.open) {
      this.close(e)
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      utils.pauseAllMedia()
      this.open = true
      document.addEventListener('keydown', this.handleEscKey.bind(this))

      if (this.hasAttribute('class-to-toggle')) {
        const elements = document.getElementsByClassName(
          this.getAttribute('class-to-toggle')
        )
        for (let element of elements) {
          element.classList.add('is-active')
        }
      }
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  close(event) {
    if (!event) return
    document.removeEventListener('keydown', this.handleEscKey.bind(this))
    if (event.type === 'click') {
      this.open = false

      if (this.hasAttribute('class-to-toggle')) {
        const elements = document.getElementsByClassName(
          this.getAttribute('class-to-toggle')
        )
        for (let element of elements) {
          element.classList.remove('is-active')
        }
      }
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <button
        @click="${this.close}"
        class="button"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        Close
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <div class="wrapper">
          <hh-focus-trap active="${this.open}">
            <slot></slot>
          </hh-focus-trap>
        </div>
      </div>
    </div>`
  }

  static styles = css`
    *:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      z-index: 2;
      top: 2rem;
      border: 0;
      right: 2rem;
      display: flex;
      cursor: pointer;
      padding: 1.2rem;
      position: fixed;
      border-radius: 0;
      align-items: center;
      color: var(--hh-color);
      justify-content: center;
      background-color: transparent;
      font: 500 14px/12px 'Monosten E', sans-serif;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      width: 100%;
      display: flex;
      height: 100vh;
      overflow: auto;
      flex-direction: column;
      padding: var(--hh-padding);
    }

    .wrapper {
      width: 100%;
      margin: 0 auto;
      max-width: 85vw;
      padding: 0 20px;
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
